'use client';

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Heart, Plus, Link as LinkIcon, Star, Trash2, Edit2, ExternalLink } from 'lucide-react';
import type { Resource } from '../../types';
import { MOCK_RESOURCES } from '../../data/mock-data';

export function ResourcesView() {
  const [resources, setResources] = useState<Resource[]>(MOCK_RESOURCES);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newResource, setNewResource] = useState({ name: '', url: '' });
  const [editResource, setEditResource] = useState({ name: '', url: '' });

  const getFavoritedItems = () => resources.filter(r => r.is_favorite);

  const addResource = () => {
    if (newResource.name && newResource.url) {
      const newItem: Resource = {
        id: `res-${Date.now()}`,
        name: newResource.name,
        url: newResource.url,
        is_favorite: false
      };
      setResources(prev => [...prev, newItem]);
      setNewResource({ name: '', url: '' });
      setShowAddForm(false);
    }
  };

  const toggleFavorite = (resourceId: string) => {
    setResources(prev =>
      prev.map(r =>
        r.id === resourceId ? { ...r, is_favorite: !r.is_favorite } : r
      )
    );
  };

  const handleEditStart = (resource: Resource) => {
    setEditingId(resource.id);
    setEditResource({
      name: resource.name,
      url: resource.url
    });
  };

  const handleEditSave = (resourceId: string) => {
    if (editResource.name && editResource.url) {
      setResources(prev =>
        prev.map(r =>
          r.id === resourceId
            ? { ...r, name: editResource.name, url: editResource.url }
            : r
        )
      );
      setEditingId(null);
      setEditResource({ name: '', url: '' });
    }
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditResource({ name: '', url: '' });
  };

  const deleteResource = (resourceId: string) => {
    if (confirm('이 학습 자료를 삭제하시겠습니까?')) {
      setResources(prev => prev.filter(r => r.id !== resourceId));
    }
  };

  const openResource = (url: string) => {
    window.open(url, '_blank');
  };

  const favoritedItems = getFavoritedItems();

  return (
    <div className="bg-white min-h-screen">
      <div className="p-4 pb-8 space-y-6">
        {/* 새 학습 자료 추가 버튼 */}
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          새 학습 자료 추가
        </Button>

        {/* 새 학습 자료 추가 폼 */}
        {showAddForm && (
          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            <input
              type="text"
              placeholder="자료 이름"
              value={newResource.name}
              onChange={(e) => setNewResource(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <input
              type="url"
              placeholder="URL (https://...)"
              value={newResource.url}
              onChange={(e) => setNewResource(prev => ({ ...prev, url: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />

            <div className="flex gap-2">
              <Button
                onClick={addResource}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                disabled={!newResource.name || !newResource.url}
              >
                추가
              </Button>
              <Button
                onClick={() => {
                  setShowAddForm(false);
                  setNewResource({ name: '', url: '' });
                }}
                variant="outline"
                className="flex-1"
              >
                취소
              </Button>
            </div>
          </div>
        )}

        {/* 찜한 학습 자료 */}
        <div>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Heart className="w-5 h-5 text-red-500" />
            즐겨찾기
          </h2>
          {favoritedItems.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Heart className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">즐겨찾기한 자료가 없습니다</p>
              <p className="text-xs text-gray-400">아래 목록에서 하트를 눌러 추가해보세요</p>
            </div>
          ) : (
            <div className="space-y-3">
              {favoritedItems.map((resource) => (
                <div
                  key={resource.id}
                  className={`border-2 border-red-200 bg-red-50 p-4 rounded-lg transition-colors ${
                    editingId === resource.id ? '' : 'cursor-pointer hover:bg-red-100'
                  }`}
                  onClick={editingId === resource.id ? undefined : () => openResource(resource.url)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {editingId === resource.id ? (
                        <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="text"
                            value={editResource.name}
                            onChange={(e) => setEditResource(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="자료 이름"
                          />
                          <input
                            type="url"
                            value={editResource.url}
                            onChange={(e) => setEditResource(prev => ({ ...prev, url: e.target.value }))}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="URL"
                          />
                          <div className="flex gap-1">
                            <Button
                              onClick={() => handleEditSave(resource.id)}
                              size="sm"
                              className="h-7 px-2 text-xs bg-emerald-500 hover:bg-emerald-600"
                            >
                              저장
                            </Button>
                            <Button
                              onClick={handleEditCancel}
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs"
                            >
                              취소
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 mb-2">
                            <Star className="w-5 h-5 text-red-600 fill-current" />
                            <span className="font-semibold text-lg text-red-900">
                              {resource.name}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 mb-1">
                            <LinkIcon className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-600 break-all">
                              {resource.url}
                            </span>
                          </div>
                        </>
                      )}
                    </div>

                    {editingId !== resource.id && (
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleEditStart(resource)}
                          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                          <Edit2 className="w-4 h-4 text-gray-500" />
                        </button>
                        <button
                          onClick={() => toggleFavorite(resource.id)}
                          className="p-2 hover:bg-red-200 rounded-full transition-colors"
                        >
                          <Heart className="w-5 h-5 text-red-500 fill-current" />
                        </button>
                        <button
                          onClick={() => deleteResource(resource.id)}
                          className="p-2 hover:bg-red-100 rounded-full transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 전체 학습 자료 목록 */}
        <div>
          <h2 className="text-lg font-semibold mb-4">모든 학습 자료</h2>
          {resources.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <LinkIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">등록된 학습 자료가 없습니다</p>
              <p className="text-sm">위 버튼을 눌러 새 학습 자료를 추가해보세요</p>
            </div>
          ) : (
            <div className="space-y-3">
              {resources.map((resource) => (
                <div
                  key={resource.id}
                  className={`border-2 border-emerald-200 bg-emerald-50 p-4 rounded-lg transition-colors ${
                    editingId === resource.id ? '' : 'cursor-pointer hover:bg-emerald-100'
                  }`}
                  onClick={editingId === resource.id ? undefined : () => openResource(resource.url)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {editingId === resource.id ? (
                        <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="text"
                            value={editResource.name}
                            onChange={(e) => setEditResource(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="자료 이름"
                          />
                          <input
                            type="url"
                            value={editResource.url}
                            onChange={(e) => setEditResource(prev => ({ ...prev, url: e.target.value }))}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="URL"
                          />
                          <div className="flex gap-1">
                            <Button
                              onClick={() => handleEditSave(resource.id)}
                              size="sm"
                              className="h-7 px-2 text-xs bg-emerald-500 hover:bg-emerald-600"
                            >
                              저장
                            </Button>
                            <Button
                              onClick={handleEditCancel}
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs"
                            >
                              취소
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 mb-2">
                            <ExternalLink className="w-5 h-5 text-emerald-600" />
                            <span className="font-semibold text-lg text-emerald-900">
                              {resource.name}
                            </span>
                            {resource.is_favorite && (
                              <Star className="w-4 h-4 text-yellow-500 fill-current" />
                            )}
                          </div>

                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm text-gray-600 break-all">
                              {resource.url}
                            </span>
                          </div>
                        </>
                      )}
                    </div>

                    {editingId !== resource.id && (
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleEditStart(resource)}
                          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                          <Edit2 className="w-4 h-4 text-gray-500" />
                        </button>
                        <button
                          onClick={() => toggleFavorite(resource.id)}
                          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                          <Heart
                            className={`w-5 h-5 ${
                              resource.is_favorite
                                ? 'text-red-500 fill-current'
                                : 'text-gray-400'
                            }`}
                          />
                        </button>
                        <button
                          onClick={() => deleteResource(resource.id)}
                          className="p-2 hover:bg-red-100 rounded-full transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
